import { Box, DialogContent, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { differenceInMinutes, differenceInSeconds } from 'date-fns'

import Search from '_components/check-in-widget/search/search.component'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import {
  BUTTON_SIZE,
  BUTTON_VARIANTS,
} from '@hapana/shared/web/components/shared/button/button.constants'
import Chip from '@hapana/shared/web/components/shared/chip/chip.component'
import { CHIP_SIZE, CHIP_VARIANT } from '@hapana/shared/web/components/shared/chip/chip.constants'
import AvatarComponent from '@hapana/shared/web/components/shared/avatar/avatar.component'
import { AVATAR_SIZE } from '@hapana/shared/web/components/shared/avatar/avatar.constants'
import TrashSmallIcon from '@hapana/shared/web/styles/assets/svgs/trash-small.svg'
import ShoppingCartSmallIcon from '@hapana/shared/web/styles/assets/svgs/shopping-cart-small.svg'
import BellSmallIcon from '@hapana/shared/web/styles/assets/svgs/bell-small.svg'
import Select from '@hapana/shared/web/components/shared/select/select.component'
import { SELECT_VARIANTS } from '@hapana/shared/web/components/shared/select/select.constants'
import { useDialog } from '@hapana/shared/web/components/shared/dialog/dialog.hooks'
import { useEquipmentQueueClients } from '_hooks/equipment-queue.hooks'
import useSiteStore from '_stores/site.store'
import Loading from '@hapana/shared/web/components/shared/loading/loading.component'
import { useListEquipments } from '_hooks/equipments.hooks'
import { useClientsByIds } from '_hooks/clients.hooks'
import { ROUTES } from '../../../../../app.constants'

import { styles } from './waitline-tab.styles.js'
import { StartTimerDialog } from './start-timer-dialog/start-timer-dialog.component'
import { NotifyClientDialog } from './notify-client-dialog/notify-client-dialog.component'
import { RemoveFromQueueDialog } from './remove-from-queue-dialog/remove-from-queue-dialog.component'
import { DEFAULT_SORTING, SORTING_OPTIONS } from './waitline-tab.constants.js'

const WaitlineTabContent = () => {
  const { t } = useTranslation(['checkIn'])
  const { pushDialog } = useDialog()
  const [searchStringValue, setSearchStringValue] = useState('')
  const [sorting, setSorting] = useState(DEFAULT_SORTING)
  const [selectedEquipmentId, setSelectedEquipmentId] = useState(null)
  const siteId = useSiteStore((state) => state.currentSite?.siteId)
  const sortAscending = sorting === SORTING_OPTIONS[1].value
  const { data: queueData, isLoading: isLoadingQueue } = useEquipmentQueueClients(
    siteId,
    selectedEquipmentId
  )
  const { data: equipmentsData, isLoading: isLoadingEquipments } = useListEquipments(siteId, {
    select: (data) => ({
      equipments: (data?.equipments || []).map((eq) => ({
        equipmentId: eq.equipmentId,
        name: eq.name,
        siteSessionTypeIds: eq.siteSessionTypeIds,
        minDelayMinutes: eq.minDelayMinutes,
        maxDelayMinutes: eq.maxDelayMinutes,
        minTimerMinutes: eq.minTimerMinutes,
        maxTimerMinutes: eq.maxTimerMinutes,
        defaultDelayMinutes: eq.defaultDelayMinutes,
        defaultTimerMinutes: eq.defaultTimerMinutes,
      })),
    }),
  })

  const rawQueueClients = useMemo(() => queueData?.equipmentQueueClients || [], [queueData])
  const clientIds = useMemo(() => rawQueueClients.map((item) => item.clientId), [rawQueueClients])
  const { data: clientsMap } = useClientsByIds(clientIds, siteId)

  const equipmentsMap = useMemo(() => {
    const map = {}
    ;(equipmentsData?.equipments || []).forEach((eq) => {
      map[eq.equipmentId] = eq
    })
    return map
  }, [equipmentsData])

  const queueClients = useMemo(() => {
    const enriched = rawQueueClients.map((item) => ({
      ...item,
      client: clientsMap?.[item.clientId] || null,
      equipment: equipmentsMap[item.equipmentId] || null,
    }))
    return sortAscending
      ? [...enriched].sort((a, b) => a.position - b.position)
      : [...enriched].sort((a, b) => b.position - a.position)
  }, [rawQueueClients, clientsMap, equipmentsMap, sortAscending])

  const filteredQueueClients = useMemo(() => {
    if (!searchStringValue) return queueClients
    const q = searchStringValue.toLowerCase()
    return queueClients.filter((item) => {
      const name = item.client?.firstName?.toLowerCase() || ''
      const email = item.client?.email?.toLowerCase() || ''
      return name.includes(q) || email.includes(q)
    })
  }, [queueClients, searchStringValue])

  const handleSearchCallback = (searchString) => {
    setSearchStringValue(searchString)
  }

  return (
    <DialogContent sx={styles.dialogContent}>
      <Box sx={styles.searchRow}>
        <Search
          searchCallback={handleSearchCallback}
          searchString={searchStringValue}
          uid="checkin"
          placeholder={t('checkIn:equipments.waitline.searchPlaceholder')}
          debounceTime={0}
        />
      </Box>
      <Box sx={styles.divider} />
      <Box sx={styles.content}>
        <Box sx={styles.header}>
          <Typography typography="header2" color="gray.80">
            {t('checkIn:equipments.waitline.queueCount', { count: filteredQueueClients.length })}
          </Typography>
          <Select
            onChange={(e) => {
              setSorting(e.target.value)
            }}
            name="test"
            defaultValue={sorting}
            options={SORTING_OPTIONS.map((opt) => ({ ...opt, label: t(opt.label) }))}
            variant={SELECT_VARIANTS.GHOST}
            selectStyles={{ maxWidth: { xs: 'none', md: 'none' }, minWidth: 0 }}
          />
        </Box>
        {isLoadingQueue || isLoadingEquipments ? (
          <Box sx={styles.centeredLoading}>
            <Loading />
          </Box>
        ) : (
          <>
            <Box sx={styles.filtersRow}>
              <Chip
                label={t('checkIn:equipments.waitline.allEquipments')}
                variant={CHIP_VARIANT.SELECTABLE_CONTAINED}
                size={CHIP_SIZE.MEDIUM}
                selected={!selectedEquipmentId}
                onClick={() => setSelectedEquipmentId(null)}
              />
              {equipmentsData?.equipments?.map((eq) => (
                <Chip
                  key={eq.equipmentId}
                  label={eq.name}
                  variant={CHIP_VARIANT.SELECTABLE_CONTAINED}
                  size={CHIP_SIZE.MEDIUM}
                  selected={selectedEquipmentId === eq.equipmentId}
                  onClick={() => setSelectedEquipmentId(eq.equipmentId)}
                />
              ))}
            </Box>
            <Box sx={styles.list}>
              {filteredQueueClients.map((item) => {
                const saleUrl = siteId
                  ? `/s/${siteId}/${ROUTES.retailDashboard.children.newSale.path}?clientId=${item.clientId}`
                  : null
                let date = null
                const createdAtDate = new Date(Number(item.createdAt))
                const mins = differenceInMinutes(new Date(), createdAtDate)
                if (mins === 0) {
                  const secs = differenceInSeconds(new Date(), createdAtDate)
                  date = t('checkIn:equipments.waitline.addedOnSeconds', { seconds: secs })
                }
                date = t('checkIn:equipments.waitline.addedOn', { date: mins })
                return (
                  <Box key={`${item.clientId}-${item.createdAt}`} sx={styles.listItem}>
                    <Box sx={styles.personInfo}>
                      <Typography typography="header3" color="primary.main">
                        {item.position}
                      </Typography>
                      <AvatarComponent
                        item={{
                          src: item.client?.profileImage?.url,
                          alt: item.client?.firstName,
                        }}
                        size={AVATAR_SIZE.SEARCH_RESULT}
                      />
                      <Box sx={styles.personDetails}>
                        <Typography typography="header3" color="gray.100">
                          {item.client?.firstName}
                        </Typography>
                        <Typography typography="medium" color="gray.64">
                          {item.client?.email}
                        </Typography>
                        <Typography typography="small" color="gray.64">
                          {date}
                        </Typography>
                        {item.equipment && (
                          <Box sx={styles.equipmentChipContainer}>
                            <Chip
                              label={item.equipment.name}
                              variant={CHIP_VARIANT.CONTAINED}
                              size={CHIP_SIZE.SMALL}
                            />
                          </Box>
                        )}
                      </Box>
                    </Box>
                    <Box sx={styles.actions}>
                      <Button
                        variant={BUTTON_VARIANTS.ICON}
                        icon={TrashSmallIcon}
                        stroke
                        size={BUTTON_SIZE.SMALL}
                        onClick={() =>
                          pushDialog(RemoveFromQueueDialog, {
                            siteId,
                            clientId: item.clientId,
                            clientName: `${item.client?.firstName} ${item.client?.lastName}`,
                            equipmentId: item.equipmentId,
                            equipmentName: item.equipment?.name,
                          })
                        }
                      />
                      <Button
                        variant={BUTTON_VARIANTS.ICON}
                        icon={ShoppingCartSmallIcon}
                        stroke
                        size={BUTTON_SIZE.SMALL}
                        href={saleUrl}
                      />
                      <Button
                        variant={BUTTON_VARIANTS.ICON}
                        icon={BellSmallIcon}
                        stroke
                        size={BUTTON_SIZE.SMALL}
                        onClick={() =>
                          pushDialog(NotifyClientDialog, {
                            clientId: item.clientId,
                            clientName: `${item.client?.firstName} ${item.client?.lastName}`,
                            equipmentId: item.equipmentId,
                            equipmentName: item.equipment?.name,
                            siteId,
                          })
                        }
                      />
                      <Button
                        variant={BUTTON_VARIANTS.CONTAINED}
                        label={t('checkIn:equipments.waitline.startTimer')}
                        size={BUTTON_SIZE.SMALL}
                        onClick={() =>
                          pushDialog(
                            StartTimerDialog,
                            {
                              clientId: item.clientId,
                              clientName: `${item.client?.firstName} ${item.client?.lastName}`,
                              equipmentId: item.equipmentId,
                              equipment: item.equipment,
                            },
                            { defaultTab: '2' }
                          )
                        }
                      />
                    </Box>
                  </Box>
                )
              })}
            </Box>
          </>
        )}
      </Box>
    </DialogContent>
  )
}

export default WaitlineTabContent
